import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@repo/design-system/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success:
          "border-transparent bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        // Deliberately not `secondary`: that maps to the near-white
        // `--secondary` token, which beside `success` reads as an unstyled
        // chip rather than the *other* half of a green-vs-grey pair. This one
        // is built like the colour variants so the contrast is explicit.
        neutral:
          "border-transparent bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
        warning:
          "border-transparent bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300",
        critical:
          "border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

// Forwards its ref so a badge can be the child of a Radix `asChild` trigger
// (a tooltip's, say) - without one, cloning the badge into the trigger drops
// the ref the trigger needs to position against.
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
